import { Camera, FileUp, ImagePlus, Loader2, Trash2, X } from 'lucide-react';
import * as React from 'react';

import { cn } from '../../lib/utils';
import { processImage } from './image-processing';
import type { IPhotoStorageAdapter, PhotoPendingItem, UploadedPhoto } from './types';

/**
 * PhotoUpload — multi-source photo upload (STORY-1103, E-125, Sally 02-munkalap
 * §2.7). Supports:
 *  - Mobile kamera (<input capture="environment">)
 *  - Desktop drag-drop zóna + Ctrl+V clipboard paste
 *  - File picker fallback
 *  - Progress + per-item error state + retry
 *  - EXIF strip + client-side canvas scale (Gate-7 D3+D4)
 *  - Storage-agnostic IPhotoStorageAdapter port (Gate-7 D1)
 *
 * Szabályok:
 *  - Kontrollált: value + onChange (sikeres upload-ok listája)
 *  - Folyamatban lévő + hibás item-ek belső state-ben (pending list)
 *  - File size limit hard-enforced (default 10 MB / fájl)
 *  - HEIC nem támogatott (Gate-7 D2, iOS Safari auto-konvertál JPEG-re)
 */

const DEFAULT_ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'application/pdf'];
const DEFAULT_MAX_FILES = 5;
const DEFAULT_MAX_SIZE_MB = 10;
const DEFAULT_COMPRESS_MAX_DIMENSION = 1920;

export interface PhotoUploadLabels {
  dropzone?: string;
  pickerButton?: string;
  cameraButton?: string;
  removeLabel?: string;
  retryLabel?: string;
  cancelLabel?: string;
  errorTooLarge?: (filename: string, maxMb: number) => string;
  errorWrongType?: (filename: string) => string;
  errorMaxFiles?: (max: number) => string;
  errorUpload?: string;
  progressAria?: (filename: string, pct: number) => string;
}

const DEFAULT_LABELS: Required<PhotoUploadLabels> = {
  dropzone: 'Ejtsd ide a fájlokat, vagy Ctrl+V beillesztés',
  pickerButton: 'Feltöltés',
  cameraButton: 'Fotó',
  removeLabel: 'Eltávolítás',
  retryLabel: 'Újra',
  cancelLabel: 'Mégse',
  errorTooLarge: (f, mb) => `${f}: túl nagy (max ${mb} MB)`,
  errorWrongType: f => `${f}: nem támogatott formátum`,
  errorMaxFiles: m => `Max ${m} fájl engedélyezett`,
  errorUpload: 'Hiba az upload közben',
  progressAria: (f, p) => `${f} feltöltve ${p}%`,
};

export interface PhotoUploadProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange'> {
  value: UploadedPhoto[];
  onChange: (photos: UploadedPhoto[]) => void;
  adapter: IPhotoStorageAdapter;
  maxFiles?: number;
  acceptedTypes?: string[];
  maxFileSizeMb?: number;
  disabled?: boolean;
  stripExif?: boolean;
  /** Max canvas dimension when re-encoding. 0 disables scale (EXIF still stripped). */
  compressMaxDimension?: number;
  labels?: PhotoUploadLabels;
  id?: string;
  'aria-describedby'?: string;
}

function genId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `ph-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

export function PhotoUpload({
  value,
  onChange,
  adapter,
  maxFiles = DEFAULT_MAX_FILES,
  acceptedTypes = DEFAULT_ACCEPTED_TYPES,
  maxFileSizeMb = DEFAULT_MAX_SIZE_MB,
  disabled = false,
  stripExif = true,
  compressMaxDimension = DEFAULT_COMPRESS_MAX_DIMENSION,
  labels,
  className,
  id,
  'aria-describedby': ariaDescribedBy,
  ...rest
}: PhotoUploadProps) {
  const L = { ...DEFAULT_LABELS, ...labels };
  const [pending, setPending] = React.useState<PhotoPendingItem[]>([]);
  const [globalError, setGlobalError] = React.useState<string | null>(null);
  const [isDragOver, setIsDragOver] = React.useState(false);

  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const cameraInputRef = React.useRef<HTMLInputElement>(null);
  const dropzoneRef = React.useRef<HTMLDivElement>(null);

  const maxBytes = maxFileSizeMb * 1024 * 1024;
  const acceptString = acceptedTypes.join(',');

  const valueRef = React.useRef(value);
  const onChangeRef = React.useRef(onChange);
  React.useEffect(() => {
    valueRef.current = value;
    onChangeRef.current = onChange;
  });

  const updatePending = React.useCallback((id: string, patch: Partial<PhotoPendingItem>) => {
    setPending(prev => prev.map(p => (p.id === id ? { ...p, ...patch } : p)));
  }, []);

  const removePending = React.useCallback((id: string) => {
    setPending(prev => {
      const item = prev.find(p => p.id === id);
      if (item) URL.revokeObjectURL(item.previewUrl);
      return prev.filter(p => p.id !== id);
    });
  }, []);

  const startUpload = React.useCallback(
    async (item: PhotoPendingItem) => {
      setPending(prev =>
        prev.map(p =>
          p.id === item.id
            ? {
                id: p.id,
                file: p.file,
                previewUrl: p.previewUrl,
                status: 'processing',
                progress: 0,
              }
            : p
        )
      );
      let processedFile: File;
      try {
        const processOpts: Parameters<typeof processImage>[1] = { stripExif };
        if (compressMaxDimension) processOpts.maxDimension = compressMaxDimension;
        processedFile = await processImage(item.file, processOpts);
      } catch {
        processedFile = item.file;
      }
      const abort = new AbortController();
      updatePending(item.id, { status: 'uploading', abort });
      try {
        const result = await adapter.upload(processedFile, {
          signal: abort.signal,
          onProgress: pct => {
            updatePending(item.id, { progress: Math.min(99, Math.round(pct)) });
          },
        });
        const newPhoto: UploadedPhoto = {
          id: result.id ?? item.id,
          url: result.url,
          fileName: processedFile.name,
          sizeBytes: processedFile.size,
          mimeType: processedFile.type,
        };
        onChangeRef.current([...valueRef.current, newPhoto]);
        removePending(item.id);
      } catch (err) {
        if (err instanceof DOMException && err.name === 'AbortError') {
          removePending(item.id);
          return;
        }
        const msg = err instanceof Error ? err.message : L.errorUpload;
        updatePending(item.id, { status: 'error', error: msg });
      }
    },
    [adapter, updatePending, removePending, stripExif, compressMaxDimension, L.errorUpload]
  );

  const queueFiles = React.useCallback(
    (rawFiles: File[]) => {
      if (disabled) return;
      const errors: string[] = [];
      const accepted: PhotoPendingItem[] = [];
      const remainingCapacity = maxFiles - (valueRef.current.length + pending.length);
      if (remainingCapacity <= 0) {
        setGlobalError(L.errorMaxFiles(maxFiles));
        return;
      }
      for (const f of rawFiles) {
        if (accepted.length >= remainingCapacity) {
          errors.push(L.errorMaxFiles(maxFiles));
          break;
        }
        if (!acceptedTypes.includes(f.type)) {
          errors.push(L.errorWrongType(f.name));
          continue;
        }
        if (f.size > maxBytes) {
          errors.push(L.errorTooLarge(f.name, maxFileSizeMb));
          continue;
        }
        accepted.push({
          id: genId(),
          file: f,
          previewUrl: URL.createObjectURL(f),
          status: 'queued',
          progress: 0,
        });
      }
      setGlobalError(errors.length > 0 ? errors.join(' · ') : null);
      if (accepted.length > 0) {
        setPending(prev => [...prev, ...accepted]);
        for (const item of accepted) void startUpload(item);
      }
    },
    [disabled, maxFiles, pending.length, acceptedTypes, maxBytes, maxFileSizeMb, startUpload, L]
  );

  React.useEffect(() => {
    const handler = (e: ClipboardEvent) => {
      if (disabled) return;
      if (!dropzoneRef.current) return;
      const active = document.activeElement;
      const scopeMatches =
        dropzoneRef.current.contains(active) || active === document.body || active === null;
      if (!scopeMatches) return;
      if (!e.clipboardData) return;
      const items = Array.from(e.clipboardData.files);
      if (items.length === 0) return;
      e.preventDefault();
      queueFiles(items);
    };
    window.addEventListener('paste', handler);
    return () => window.removeEventListener('paste', handler);
  }, [disabled, queueFiles]);

  React.useEffect(() => {
    return () => {
      for (const p of pending) URL.revokeObjectURL(p.previewUrl);
    };
  }, [pending]);

  const handleFilePickerChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    queueFiles(files);
    e.target.value = '';
  };

  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    if (disabled) return;
    e.preventDefault();
    setIsDragOver(true);
  };
  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    if (disabled) return;
    e.preventDefault();
  };
  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    if (disabled) return;
    if (e.currentTarget === e.target) setIsDragOver(false);
  };
  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    if (disabled) return;
    e.preventDefault();
    setIsDragOver(false);
    const files = Array.from(e.dataTransfer.files);
    queueFiles(files);
  };

  const removeValue = (photoId: string) => {
    if (disabled) return;
    onChange(value.filter(p => p.id !== photoId));
  };

  const cancelPending = (pendingId: string) => {
    const item = pending.find(p => p.id === pendingId);
    if (!item) return;
    if (item.abort) item.abort.abort();
    removePending(pendingId);
  };

  const retryPending = (pendingId: string) => {
    const item = pending.find(p => p.id === pendingId);
    if (!item) return;
    void startUpload(item);
  };

  return (
    <div
      ref={dropzoneRef}
      className={cn('flex flex-col gap-2', className)}
      data-testid="photo-upload"
      data-disabled={disabled ? 'true' : undefined}
      aria-describedby={ariaDescribedBy}
      {...rest}
    >
      <div
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-disabled={disabled}
        aria-label={L.dropzone}
        data-testid="photo-upload-dropzone"
        data-dragover={isDragOver ? 'true' : undefined}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={() => !disabled && fileInputRef.current?.click()}
        onKeyDown={e => {
          if ((e.key === 'Enter' || e.key === ' ') && !disabled) {
            e.preventDefault();
            fileInputRef.current?.click();
          }
        }}
        className={cn(
          'flex flex-col items-center justify-center gap-2 rounded-md border border-dashed border-border p-4 text-sm text-muted-foreground outline-none transition-colors',
          'focus-visible:ring-2 focus-visible:ring-ring',
          !disabled && 'cursor-pointer hover:border-primary/60 hover:text-foreground',
          disabled && 'cursor-not-allowed opacity-60',
          isDragOver && 'border-primary bg-primary/5 text-foreground'
        )}
      >
        <ImagePlus size={20} aria-hidden="true" />
        <span>{L.dropzone}</span>
        <div className="flex flex-wrap items-center gap-2 pt-1">
          <button
            type="button"
            data-testid="photo-upload-camera-trigger"
            onClick={e => {
              e.stopPropagation();
              cameraInputRef.current?.click();
            }}
            disabled={disabled}
            className="inline-flex items-center gap-1 rounded-md border border-border bg-background px-2 py-1 text-xs hover:bg-accent disabled:opacity-50"
          >
            <Camera size={14} aria-hidden="true" />
            {L.cameraButton}
          </button>
          <button
            type="button"
            data-testid="photo-upload-picker-trigger"
            onClick={e => {
              e.stopPropagation();
              fileInputRef.current?.click();
            }}
            disabled={disabled}
            className="inline-flex items-center gap-1 rounded-md border border-border bg-background px-2 py-1 text-xs hover:bg-accent disabled:opacity-50"
          >
            <FileUp size={14} aria-hidden="true" />
            {L.pickerButton}
          </button>
        </div>
        <span className="text-xs text-muted-foreground">
          {value.length + pending.length}/{maxFiles}
        </span>
      </div>

      <input
        ref={fileInputRef}
        id={id}
        type="file"
        multiple
        accept={acceptString}
        onChange={handleFilePickerChange}
        disabled={disabled}
        className="hidden"
        data-testid="photo-upload-file-input"
      />
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handleFilePickerChange}
        disabled={disabled}
        className="hidden"
        data-testid="photo-upload-camera-input"
      />

      {globalError && (
        <p role="alert" data-testid="photo-upload-error" className="text-xs text-red-600">
          {globalError}
        </p>
      )}

      {(value.length > 0 || pending.length > 0) && (
        <ul
          data-testid="photo-upload-list"
          className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4"
        >
          {value.map(photo => (
            <li
              key={photo.id}
              data-testid="photo-upload-item"
              data-item-id={photo.id}
              data-status="uploaded"
              className="group relative overflow-hidden rounded-md border border-border"
            >
              {photo.mimeType.startsWith('image/') ? (
                <img src={photo.url} alt={photo.fileName} className="h-24 w-full object-cover" />
              ) : (
                <div className="flex h-24 items-center justify-center bg-muted text-xs">PDF</div>
              )}
              <button
                type="button"
                aria-label={`${L.removeLabel}: ${photo.fileName}`}
                onClick={() => removeValue(photo.id)}
                disabled={disabled}
                className="absolute right-1 top-1 rounded-full bg-background/80 p-1 text-foreground opacity-0 transition-opacity group-hover:opacity-100 focus-visible:opacity-100"
              >
                <Trash2 size={14} aria-hidden="true" />
              </button>
            </li>
          ))}
          {pending.map(item => (
            <li
              key={item.id}
              data-testid="photo-upload-item"
              data-item-id={item.id}
              data-status={item.status}
              className="relative overflow-hidden rounded-md border border-border"
            >
              {item.file.type.startsWith('image/') ? (
                <img
                  src={item.previewUrl}
                  alt={item.file.name}
                  className="h-24 w-full object-cover opacity-70"
                />
              ) : (
                <div className="flex h-24 items-center justify-center bg-muted text-xs">PDF</div>
              )}
              <div
                role="progressbar"
                aria-valuemin={0}
                aria-valuemax={100}
                aria-valuenow={item.progress}
                aria-label={L.progressAria(item.file.name, item.progress)}
                data-testid="photo-upload-progress"
                className="absolute inset-x-0 bottom-0 h-1 bg-background/40"
              >
                <div
                  className={cn('h-full', item.status === 'error' ? 'bg-red-500' : 'bg-primary')}
                  style={{ width: `${item.progress}%` }}
                />
              </div>
              {item.status !== 'error' && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <Loader2 size={20} className="animate-spin text-primary" aria-hidden="true" />
                </div>
              )}
              {item.status === 'error' && (
                <div className="absolute inset-x-0 bottom-1 flex justify-center gap-1 px-1 text-xs">
                  <button
                    type="button"
                    aria-label={L.retryLabel}
                    onClick={() => retryPending(item.id)}
                    className="rounded bg-background/90 px-2 py-0.5 hover:bg-background"
                  >
                    {L.retryLabel}
                  </button>
                  <button
                    type="button"
                    aria-label={L.cancelLabel}
                    onClick={() => cancelPending(item.id)}
                    className="rounded bg-background/90 px-2 py-0.5 hover:bg-background"
                  >
                    {L.cancelLabel}
                  </button>
                </div>
              )}
              {item.status !== 'error' && (
                <button
                  type="button"
                  aria-label={L.cancelLabel}
                  onClick={() => cancelPending(item.id)}
                  className="absolute right-1 top-1 rounded-full bg-background/80 p-1 text-foreground"
                >
                  <X size={14} aria-hidden="true" />
                </button>
              )}
              {item.error && (
                <p
                  data-testid="photo-upload-item-error"
                  className="absolute inset-x-0 top-0 truncate bg-red-500/90 px-1 py-0.5 text-[10px] text-white"
                  title={item.error}
                >
                  {item.error}
                </p>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
